import React, { useState, useEffect, useCallback } from 'react';
import { leadService } from '../services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Search, RefreshCw, Users } from 'lucide-react';
import { toast } from 'sonner';
import KanbanBoard from '../components/leads/KanbanBoard';
import LeadDetailPanel from '../components/leads/LeadDetailPanel';

const SOURCE_OPTIONS = [
  { value: 'all', label: 'Todos los canales' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'web', label: 'Web' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'manual', label: 'Manual' },
];

export default function LeadsPage() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [selectedLead, setSelectedLead] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);

  const loadLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (sourceFilter !== 'all') params.source = sourceFilter;
      if (search.trim()) params.search = search.trim();
      const data = await leadService.getAll(params);
      setLeads(data);
    } catch {
      toast.error('Error al cargar leads');
    } finally {
      setLoading(false);
    }
  }, [sourceFilter, search]);

  useEffect(() => {
    const id = setTimeout(loadLeads, 300);
    return () => clearTimeout(id);
  }, [loadLeads]);

  useEffect(() => {
    const interval = setInterval(loadLeads, 30000);
    return () => clearInterval(interval);
  }, [loadLeads]);

  const handleStatusChange = async (leadId, newStatus, previousStatus) => {
    // Update locally before the server responds to keep the UI responsive
    setLeads((prev) =>
      prev.map((l) => (l.id === leadId ? { ...l, status: newStatus } : l))
    );
    try {
      await leadService.update(leadId, { status: newStatus });
    } catch {
      // Server rejected the change — roll back to the last known-good state
      setLeads((prev) =>
        prev.map((l) => (l.id === leadId ? { ...l, status: previousStatus } : l))
      );
      toast.error('Error al mover el lead');
    }
  };

  const handleCardClick = (lead) => {
    setSelectedLead(lead);
    setPanelOpen(true);
  };

  const handleLeadUpdated = (updatedLead) => {
    setLeads((prev) =>
      prev.map((l) => (l.id === updatedLead.id ? updatedLead : l))
    );
    setSelectedLead(updatedLead);
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6">
        <div className="flex-1">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">CRM Leads</h1>
          <p className="text-slate-500 text-sm mt-0.5">
            {leads.length} lead{leads.length !== 1 ? 's' : ''} en total
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Nombre o teléfono..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 h-9 w-48 text-sm"
            />
          </div>

          <Select value={sourceFilter} onValueChange={setSourceFilter}>
            <SelectTrigger className="h-9 w-44 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SOURCE_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button
            variant="outline"
            size="sm"
            onClick={loadLeads}
            disabled={loading}
            className="h-9"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {loading && leads.length === 0 ? (
        <div className="flex items-center justify-center py-24 text-slate-400">
          <RefreshCw className="w-6 h-6 animate-spin mr-2" />
          Cargando leads...
        </div>
      ) : leads.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 text-slate-400 gap-3">
          <Users className="w-12 h-12" />
          <p className="text-lg font-medium">No hay leads todavía</p>
          <p className="text-sm text-center max-w-sm">
            Los leads se crean automáticamente cuando los clientes contactan a través de
            WhatsApp u otros canales.
          </p>
        </div>
      ) : (
        <KanbanBoard
          leads={leads}
          onStatusChange={handleStatusChange}
          onCardClick={handleCardClick}
          onLeadUpdated={handleLeadUpdated}
        />
      )}

      <LeadDetailPanel
        lead={selectedLead}
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        onLeadUpdated={handleLeadUpdated}
      />
    </div>
  );
}
